/**
 * The rule of the light identity, the TypeScript twin of balancegen
 * (ui-map-interface.md §2.5, R3293).
 *
 * balancegen computes every light map ink from its dark ink with this
 * rule and writes the pair into the generated modules; Go is the source
 * of truth. This twin repeats the rule step by step on the OKLab maths of
 * `ui/theme.ts` (R255), so `lightrule.test.ts` recomputes every pair of
 * `gen/inks.ts` to the byte, and a later surface (a custom theme, the
 * light chrome of lane K) can map an ink that the data layer does not
 * carry. The render loop never calls it.
 *
 * The rule (`ladder_own_chroma`, mockup round 148 card 05):
 *
 *   - a paper role takes the ladder lightness of its role and the paper
 *     hue and chroma;
 *   - a faction ink takes the faction lightness, its own chroma times
 *     `chromaK`, and the hue of `factionHues`;
 *   - the `ramp` role maps the dark lightness rank of the steps of a list,
 *     inverted, onto `[ramp.lo, ramp.hi]` (the lightest dark step takes
 *     `ramp.lo`) and keeps each step's chroma and hue;
 *   - every other ink takes the ladder lightness of its role and keeps
 *     its own chroma and hue;
 *   - then `fit` gives up chroma, never lightness.
 */

import {
  contrastRatio, derivedDim, fit, hexOf, labOf, oklch, over, rgbOfHex, type Lch, type Rgb,
} from './theme';

/** The parameters of the rule: `LIGHT_RULE` of `gen/inks.ts`. */
export interface LightRule {
  readonly rule: string;
  readonly ladder: Readonly<Record<string, number>>;
  readonly paperH: number;
  readonly paperC: number;
  readonly paperRoles: readonly string[];
  readonly hueMid: readonly number[];
  readonly factionL: number;
  readonly chromaK: number;
  readonly glowAlphaK: number;
  readonly ramp: { readonly lo: number; readonly hi: number };
}

/** One faction and its dark ink. */
export interface FactionInk {
  readonly id: number;
  readonly dark: number;
}

/** The role of a step of an ordered ink list. */
export const RAMP_ROLE = 'ramp';

/** The role of a faction ink. */
export const FACTION_ROLE = 'faction';

function rgbOfInk(c: number): Rgb {
  return [(c >> 16) & 0xff, (c >> 8) & 0xff, c & 0xff];
}

function inkOfRgb([r, g, b]: Rgb): number {
  return (r << 16) | (g << 8) | b;
}

function inkOfLch(c: Lch): number {
  return inkOfRgb(fit(labOf(c)));
}

/**
 * The light hue of each faction: each faction keeps the hue of its dark
 * ink, and a faction of `hueMid` takes the mid hue of its two neighbours
 * on the wheel. The arithmetic follows balancegen step by step.
 */
export function factionHues(rule: LightRule, factions: readonly FactionInk[]): Map<number, number> {
  const h = new Map<number, number>();
  for (const f of factions) h.set(f.id, (oklch(rgbOfInk(f.dark))[2] + 360) % 360);
  for (const id of rule.hueMid) {
    const own = h.get(id);
    if (own === undefined) continue;
    let up = Infinity;
    let dn = Infinity;
    for (const f of factions) {
      if (f.id === id) continue;
      const o = h.get(f.id) ?? 0;
      up = Math.min(up, (o - own + 360) % 360);
      dn = Math.min(dn, (own - o + 360) % 360);
    }
    const a = own + up;
    const b = own - dn;
    h.set(id, (((a + b) / 2) % 360 + 360) % 360);
  }
  return h;
}

/**
 * The light ink of one dark ink of one role. A faction ink must be the
 * dark ink of one of `factions`; any other ink of the faction role
 * throws. The ramp role takes a list: call `lightRamp`.
 */
export function lightInk(
  rule: LightRule, factions: readonly FactionInk[], hues: ReadonlyMap<number, number>,
  role: string, dark: number,
): number {
  const [, C, H] = oklch(rgbOfInk(dark));
  if (rule.paperRoles.includes(role)) return inkOfLch([rule.ladder[role] ?? 0, rule.paperC, rule.paperH]);
  if (role === FACTION_ROLE) {
    const f = factions.find((x) => x.dark === dark);
    if (f === undefined) throw new Error(`lightrule: ${dark.toString(16)} is no faction ink`);
    return inkOfLch([rule.factionL, C * rule.chromaK, hues.get(f.id) ?? H]);
  }
  if (role === RAMP_ROLE) throw new Error('lightrule: the ramp role takes a list (lightRamp)');
  const L = rule.ladder[role];
  if (L === undefined) throw new Error(`lightrule: ${role} is no role`);
  return inkOfLch([L, C, H]);
}

/**
 * The light inks of an ordered list, the dark rank inverted: the lightest
 * dark step takes `ramp.lo`, the darkest `ramp.hi`, and the steps between
 * them stand evenly by rank. A bright ink of the dark identity is a dark
 * ink on paper, so the strongest step stays the one that stands out (the
 * lead's ruling, corrected 2026-11-05, R3295). A tie keeps the list
 * order; a list of one step takes the middle.
 */
export function lightRamp(rule: LightRule, darks: readonly number[]): number[] {
  const { lo, hi } = rule.ramp;
  const n = darks.length;
  const lch = darks.map((d) => oklch(rgbOfInk(d)));
  const order = darks.map((_, i) => i).sort((a, b) => lch[a]![0] - lch[b]![0] || a - b);
  const out = new Array<number>(n);
  order.forEach((i, rank) => {
    const L = n > 1 ? hi - ((hi - lo) * rank) / (n - 1) : (lo + hi) / 2;
    out[i] = inkOfLch([L, lch[i]![1], lch[i]![2]]);
  });
  return out;
}

/** One map ink as the generator lists it: `INK_PAIRS` of `gen/inks.ts`. */
export interface RuledInk {
  readonly path: string;
  readonly role: string;
  readonly dark: number;
}

/**
 * The light ink of every row of `inks`, in order. The steps of a ramp
 * list share the path `<list>.<i>`, so a ramp row is ranked among the
 * rows of its own list.
 */
export function lightInks(rule: LightRule, factions: readonly FactionInk[], inks: readonly RuledInk[]): number[] {
  const hues = factionHues(rule, factions);
  const lists = new Map<string, number[]>();
  inks.forEach((p, i) => {
    if (p.role !== RAMP_ROLE) return;
    const list = p.path.slice(0, p.path.lastIndexOf('.'));
    const rows = lists.get(list) ?? [];
    rows.push(i);
    lists.set(list, rows);
  });
  const out = new Array<number>(inks.length);
  for (const rows of lists.values()) {
    const lights = lightRamp(rule, rows.map((i) => inks[i]!.dark));
    rows.forEach((i, k) => { out[i] = lights[k]!; });
  }
  inks.forEach((p, i) => {
    if (p.role !== RAMP_ROLE) out[i] = lightInk(rule, factions, hues, p.role, p.dark);
  });
  return out;
}

// --- the light chrome (ui-map-interface.md §2.5 "The chrome", R3298) -------
//
// The twin of balancegen's light_chrome.go. A ground role takes the ladder
// lightness and the OKLab chroma vector `paperW` x the paper plus `themeW`
// x the own chroma and hue of its dark token. An ink role takes the
// ladder lightness and its own chroma and hue, and its lightness goes down
// by the nudge step until it clears its floor on the panel ground and on
// the sheet. The dim ink is `derivedDim` of the light text and faint
// (R1776). `ui/lightchrome.ts` reads the generated bodies; this twin
// computes the light body of a custom UI theme at load.

/** The parameters of the chrome rule: `LIGHT_CHROME_RULE` of `gen/inks.ts`. */
export interface LightChromeRule {
  readonly paperH: number;
  readonly paperC: number;
  readonly ground: { readonly paperW: number; readonly themeW: number };
  readonly ladder: { readonly panel: number; readonly sheet: number; readonly ink: number; readonly deep: number };
  readonly panelAlpha: number;
  readonly inkLadder: { readonly text: number; readonly faint: number; readonly accent: number };
  readonly edge: { readonly ink: string; readonly alpha: number };
  readonly frozenL: number;
  readonly floors: { readonly text: number; readonly faint: number; readonly accent: number; readonly frozen: number };
  readonly nudge: number;
  readonly worst: number;
}

/** A UI theme body as the chrome rule reads it: the shape of `ThemeBody`. */
export interface ChromeInks {
  readonly accent: string;
  readonly text: string;
  readonly faint: string;
  readonly panel: string;
  readonly sheet: string;
  readonly ink: string;
  readonly deep: string;
  readonly dim?: string;
  readonly panelAlpha: number;
  readonly edgeAlpha: number;
}

/** The light body of one UI theme: every ink stated. */
export interface LightChromeBody extends ChromeInks {
  readonly dim: string;
}

/** The panel ground and the sheet ground an ink must clear its floor on. */
export interface ChromeGrounds {
  readonly ground: Rgb;
  readonly sheet: Rgb;
}

/** The ground rule: the lightness L, and the OKLab chroma vector `paperW`
 *  x the paper plus `themeW` x the own chroma and hue of `dark`. */
export function chromeGround(rule: LightChromeRule, L: number, dark: string): string {
  const [, C, H] = oklch(rgbOfHex(dark));
  const pr = (rule.paperH * Math.PI) / 180;
  const tr = (H * Math.PI) / 180;
  const g = rule.ground;
  const a = g.paperW * rule.paperC * Math.cos(pr) + g.themeW * C * Math.cos(tr);
  const b = g.paperW * rule.paperC * Math.sin(pr) + g.themeW * C * Math.sin(tr);
  return hexOf(fit([L, a, b]));
}

function chromeInkAt(L: number, src: string): Rgb {
  const [, C, H] = oklch(rgbOfHex(src));
  return fit(labOf([L, C, H]));
}

/** The panel ground (the panel at the panel alpha over the light ridge
 *  rock) and the sheet ground of one light body. */
export function chromeGrounds(rule: LightChromeRule, b: { panel: string; sheet: string }): ChromeGrounds {
  const ground = over(rgbOfHex(b.panel), rgbOfInk(rule.worst), rule.panelAlpha);
  return { ground, sheet: over(rgbOfHex(b.sheet), ground, rule.panelAlpha) };
}

/**
 * The floor of the rule: the ink of `src` at the lightness L, then L goes
 * down by the nudge step until the ink clears `floor` on every ground of
 * `gs`, at most 60 steps.
 */
export function chromeFloorInk(
  rule: LightChromeRule, L: number, src: string, floor: number, gs: readonly ChromeGrounds[],
): string {
  const clears = (c: Rgb) => gs.every((g) => contrastRatio(c, g.ground) >= floor && contrastRatio(c, g.sheet) >= floor);
  let out = chromeInkAt(L, src);
  for (let n = 1; !clears(out) && n <= 60; n++) out = chromeInkAt(L - rule.nudge * n, src);
  return hexOf(out);
}

/**
 * The light body of one dark UI theme body (round 149). `accentSrc` is the
 * ink the light accent takes its hue and chroma from: the light faction
 * ink for AUTO and a faction theme, the own accent for any other theme.
 */
export function lightChromeBody(rule: LightChromeRule, dark: ChromeInks, accentSrc: string): LightChromeBody {
  const panel = chromeGround(rule, rule.ladder.panel, dark.panel);
  const sheet = chromeGround(rule, rule.ladder.sheet, dark.sheet);
  const gs = [chromeGrounds(rule, { panel, sheet })];
  const text = chromeFloorInk(rule, rule.inkLadder.text, dark.text, rule.floors.text, gs);
  const faint = chromeFloorInk(rule, rule.inkLadder.faint, dark.faint, rule.floors.faint, gs);
  return {
    accent: chromeFloorInk(rule, rule.inkLadder.accent, accentSrc, rule.floors.accent, gs),
    text,
    dim: derivedDim(text, faint),
    faint,
    panel,
    sheet,
    ink: chromeGround(rule, rule.ladder.ink, dark.ink),
    deep: chromeGround(rule, rule.ladder.deep, dark.deep),
    panelAlpha: rule.panelAlpha,
    edgeAlpha: rule.edge.alpha,
  };
}
